import React, { useState } from 'react';
import { BookOpen, Play, Eye, Download } from 'lucide-react';
import { allSamples } from '../data/knowledgeGraph';

const HKDSEMathsVisual = ({ onSampleSelect }) => {
  const [selectedSample, setSelectedSample] = useState(null);

  // Simple LaTeX renderer for problem text
  const renderLatexText = (text) => {
    if (!text) return '';
    
    // Simple LaTeX to HTML conversion for basic expressions
    let processedText = text;
    
    // Convert LaTeX fractions
    processedText = processedText.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="frac"><span class="num">$1</span><span class="denom">$2</span></span>');
    
    // Convert LaTeX superscripts
    processedText = processedText.replace(/\^(\d+)/g, '<sup>$1</sup>');
    processedText = processedText.replace(/\^\{([^}]+)\}/g, '<sup>$1</sup>');
    
    // Convert LaTeX subscripts
    processedText = processedText.replace(/_(\d+)/g, '<sub>$1</sub>');
    processedText = processedText.replace(/_\{([^}]+)\}/g, '<sub>$1</sub>');
    
    // Convert LaTeX symbols
    processedText = processedText.replace(/\\alpha/g, 'α');
    processedText = processedText.replace(/\\beta/g, 'β');
    processedText = processedText.replace(/\\gamma/g, 'γ');
    processedText = processedText.replace(/\\delta/g, 'δ');
    processedText = processedText.replace(/\\theta/g, 'θ');
    processedText = processedText.replace(/\\pi/g, 'π');
    processedText = processedText.replace(/\\infty/g, '∞');
    processedText = processedText.replace(/\\leq/g, '≤');
    processedText = processedText.replace(/\\geq/g, '≥');
    processedText = processedText.replace(/\\neq/g, '≠');
    processedText = processedText.replace(/\\approx/g, '≈');
    processedText = processedText.replace(/\\pm/g, '±');
    processedText = processedText.replace(/\\times/g, '×');
    processedText = processedText.replace(/\\div/g, '÷');
    processedText = processedText.replace(/\\sqrt/g, '√');
    processedText = processedText.replace(/\\sum/g, '∑');
    processedText = processedText.replace(/\\int/g, '∫');
    
    // Convert dollar signs to math mode
    processedText = processedText.replace(/\$([^$]+)\$/g, '<span class="math-mode">$1</span>');
    
    return processedText;
  };

  // Only HKDSE samples
  const hkdseSamples = [
    {
      id: 'hkdse2025',
      title: 'HKDSE Paper 1 2025',
      description: 'HKDSE Mathematics Paper 1 2025 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2024',
      title: 'HKDSE Paper 1 2024',
      description: 'HKDSE Mathematics Paper 1 2024 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2023',
      title: 'HKDSE Paper 1 2023',
      description: 'HKDSE Mathematics Paper 1 2023 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2022',
      title: 'HKDSE Paper 1 2022',
      description: 'HKDSE Mathematics Paper 1 2022 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2021',
      title: 'HKDSE Paper 1 2021',
      description: 'HKDSE Mathematics Paper 1 2021 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2020',
      title: 'HKDSE Paper 1 2020',
      description: 'HKDSE Mathematics Paper 1 2020 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2019',
      title: 'HKDSE Paper 1 2019',
      description: 'HKDSE Mathematics Paper 1 2019 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2018',
      title: 'HKDSE Paper 1 2018',
      description: 'HKDSE Mathematics Paper 1 2018 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2017',
      title: 'HKDSE Paper 1 2017',
      description: 'HKDSE Mathematics Paper 1 2017 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2016',
      title: 'HKDSE Paper 1 2016',
      description: 'HKDSE Mathematics Paper 1 2016 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    },
    {
      id: 'hkdse2015',
      title: 'HKDSE Paper 1 2015',
      description: 'HKDSE Mathematics Paper 1 2015 questions with knowledge graphs',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.hkdse
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      purple: 'bg-purple-50 border-purple-200 text-purple-800'
    };
    return colorMap[color] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const renderSampleCard = (sample) => {
    const Icon = sample.icon;
    const colorClasses = getColorClasses(sample.color);
    
    // Handle HKDSE samples - get samples for the specific year
    const year = sample.id.replace('hkdse', '');
    const yearKey = `hkdse${year}`;
    const yearSamples = allSamples.hkdse[yearKey] || {};
    
    const allHkdseSamples = [];
    Object.values(yearSamples).forEach(topicSamples => {
      if (Array.isArray(topicSamples)) {
        allHkdseSamples.push(...topicSamples);
      }
    });
    
    const currentDifficultySamples = allHkdseSamples;
    
    // Override question count for specific years based on LaTeX files
    let sampleCount = allHkdseSamples.length;
    if (year === '2025') sampleCount = 19; // LaTeX file has 19 questions
    else if (year === '2024') sampleCount = 5; // Current data has 5 questions
    else if (year === '2023') sampleCount = 5; // Current data has 5 questions
    else if (year === '2022') sampleCount = 15; // Current data has 15 questions
    else if (year === '2021') sampleCount = 19; // LaTeX file has 19 questions
    else if (year === '2020') sampleCount = 3; // Current data has 3 questions
    else if (year === '2019') sampleCount = 2; // Current data has 2 questions
    else if (year === '2018') sampleCount = 3; // Current data has 3 questions
    else if (year === '2017') sampleCount = 3; // Current data has 3 questions
    else if (year === '2016') sampleCount = 4; // Current data has 4 questions
    else if (year === '2015') sampleCount = 4; // Current data has 4 questions
    const firstSample = allHkdseSamples[0];

    return (
      <div key={sample.id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${colorClasses}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{sample.title}</h3>
              <p className="text-sm text-gray-600">{sample.description}</p>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Available Questions</span>
              <span className="font-medium">{sampleCount}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((sampleCount / 10) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {firstSample ? (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Sample Question:</h4>
              <div 
                className="text-sm text-gray-700 line-clamp-2"
                dangerouslySetInnerHTML={{ __html: renderLatexText(firstSample.problem) }}
              />
            </div>
          ) : (
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">No Questions Available</h4>
              <p className="text-sm text-yellow-700">
                Sample questions for this year are not yet available.
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => {
                setSelectedSample(sample);
                if (onSampleSelect && firstSample) {
                  onSampleSelect(firstSample);
                }
              }}
              disabled={!firstSample}
              className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                firstSample 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Play className="w-4 h-4" />
              {firstSample ? 'Start Practice' : 'No Questions'}
            </button>
            <button 
              className={`p-2 rounded-lg transition-colors ${
                firstSample 
                  ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!firstSample}
            >
              <Eye className="w-4 h-4" />
            </button>
            <button 
              className={`p-2 rounded-lg transition-colors ${
                firstSample 
                  ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!firstSample}
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSamplePreview = () => {
    if (!selectedSample) return null;

    const year = selectedSample.id.replace('hkdse', '');
    const yearKey = `hkdse${year}`;
    const yearSamples = allSamples.hkdse[yearKey] || {};
    
    const allHkdseSamples = [];
    Object.values(yearSamples).forEach(topicSamples => {
      if (Array.isArray(topicSamples)) {
        allHkdseSamples.push(...topicSamples);
      }
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{selectedSample.title}</h2>
            <button
              onClick={() => setSelectedSample(null)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>

          {allHkdseSamples.length > 0 ? (
            <div className="grid gap-4">
              {allHkdseSamples.map((sample, index) => (
                <div key={sample.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">Question {index + 1}</h3>
                    <span className="text-sm text-gray-500 capitalize">{sample.difficulty}</span>
                  </div>
                  <div 
                    className="text-gray-700 mb-3"
                    dangerouslySetInnerHTML={{ __html: renderLatexText(sample.problem) }}
                  />
                  <div className="flex gap-2">
                    <button className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 transition-colors">
                      View Knowledge Graph
                    </button>
                    <button className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-200 transition-colors">
                      Practice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 text-lg mb-2">No Questions Available</div>
              <p className="text-gray-400">Sample questions for this year are not yet available.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Add LaTeX styles */}
      <style>
        {`
          .frac {
            display: inline-block;
            vertical-align: middle;
            text-align: center;
            margin: 0 2px;
          }
          .frac .num {
            display: block;
            border-bottom: 1px solid;
            padding: 0 2px;
            font-size: 0.9em;
          }
          .frac .denom {
            display: block;
            padding: 0 2px;
            font-size: 0.9em;
          }
          .math-mode {
            font-family: 'Computer Modern', serif;
            font-style: italic;
          }
          sup, sub {
            font-size: 0.75em;
            line-height: 1;
          }
        `}
      </style>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">HKDSE Mathematics Paper 1</h2>
        <p className="text-gray-600">Select a year to practice with knowledge graph visualization</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {hkdseSamples.map(renderSampleCard)}
      </div>

      {renderSamplePreview()}
    </div>
  );
};

export default HKDSEMathsVisual; 